/**
 * Fascicolo completo contenzioso: dati ordine/fornitore inline,
 * importi, saldo, timeline azioni con possibilità di aggiungere.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card } from '@floraltech/ui';
import { useFormatters } from '@lib/formatters';

interface Azione {
  id: string;
  contenzioso_id: string;
  tipo_azione: string;
  descrizione: string;
  importo: string | null;
  documento_path: string | null;
  operatore_id: string;
  created_at: string;
}

interface ContenziosoData {
  id: string;
  ricezione_id: string;
  ordine_id: string | null;
  fornitore_id: string | null;
  numero: string | null;
  anno: number;
  tipo: string;
  stato: string;
  importo_contestato: string | null;
  importo_risolto: string | null;
  valuta: string;
  note: string;
  esito: string | null;
  chiuso_at: string | null;
  created_at: string;
  updated_at: string;
  azioni: Azione[];
}

const TIPO_AZIONE_LABELS: Record<string, string> = {
  nota: 'Nota',
  comunicazione: 'Comunicazione',
  nota_credito: 'Nota di credito',
  reso: 'Reso',
  sostituzione: 'Sostituzione',
  rimborso: 'Rimborso',
  allegato: 'Allegato',
};

const STATO_BADGE: Record<string, { label: string; className: string }> = {
  aperto: { label: 'Aperto', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
  in_gestione: { label: 'In gestione', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  risolto: { label: 'Risolto', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  chiuso_senza_esito: { label: 'Chiuso senza esito', className: 'bg-muted text-muted-foreground' },
};

export function ContenziosoDetail() {
  const { id } = useParams<{ id: string }>();
  const { currency, date: fmtDate } = useFormatters();
  const token = localStorage.getItem('token');

  const [contenzioso, setContenzioso] = useState<ContenziosoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Form nuova azione
  const [showAzioneForm, setShowAzioneForm] = useState(false);
  const [tipoAzione, setTipoAzione] = useState('nota');
  const [descrizione, setDescrizione] = useState('');
  const [importoAzione, setImportoAzione] = useState('');

  // Form risoluzione
  const [showRisoluzione, setShowRisoluzione] = useState(false);
  const [esitoRis, setEsitoRis] = useState('');
  const [importoRisolto, setImportoRisolto] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`/api/v1/ordini/contenziosi/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) setContenzioso(await res.json());
    setLoading(false);
  }, [id, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAggiungiAzione = async () => {
    if (!descrizione.trim()) return;
    setActionLoading(true);

    const body: Record<string, unknown> = {
      tipo_azione: tipoAzione,
      descrizione: descrizione.trim(),
    };
    if (importoAzione) body.importo = importoAzione;

    await fetch(`/api/v1/ordini/contenziosi/${id}/azioni`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    setDescrizione('');
    setImportoAzione('');
    setShowAzioneForm(false);
    await fetchData();
    setActionLoading(false);
  };

  const handleRisolvi = async () => {
    if (!esitoRis.trim()) return;
    setActionLoading(true);

    const body: Record<string, unknown> = { esito: esitoRis.trim() };
    if (importoRisolto) body.importo_risolto = importoRisolto;

    await fetch(`/api/v1/ordini/contenziosi/${id}/risolvi`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    setShowRisoluzione(false);
    await fetchData();
    setActionLoading(false);
  };

  if (loading || !contenzioso) {
    return <div className="p-6 text-muted-foreground">Caricamento…</div>;
  }

  const badge = STATO_BADGE[contenzioso.stato] || { label: contenzioso.stato, className: 'bg-muted text-muted-foreground' };
  const isOpen = contenzioso.stato === 'aperto' || contenzioso.stato === 'in_gestione';

  return (
    <div className="p-6 space-y-6 w-full">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-outfit font-semibold text-foreground">
            Contenzioso {contenzioso.numero || `#${contenzioso.id.slice(0, 8)}`}
          </h1>
          <div className="flex items-center gap-3 mt-1">
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badge.className}`}>
              {badge.label}
            </span>
            <span className="text-sm text-muted-foreground capitalize">{contenzioso.tipo.replace(/_/g, ' ')}</span>
            <span className="text-sm text-muted-foreground">{fmtDate(contenzioso.created_at)}</span>
          </div>
        </div>

        {isOpen && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowAzioneForm(true)} disabled={actionLoading}>
              Aggiungi azione
            </Button>
            <Button onClick={() => setShowRisoluzione(true)} disabled={actionLoading}>
              Risolvi
            </Button>
          </div>
        )}
      </div>

      {/* Info fascicolo */}
      <Card className="p-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Importo contestato:</span>{' '}
            <span className="font-semibold">
              {contenzioso.importo_contestato ? currency(contenzioso.importo_contestato) : '—'}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Importo risolto:</span>{' '}
            <span className="font-semibold">
              {contenzioso.importo_risolto ? currency(contenzioso.importo_risolto) : '—'}
            </span>
          </div>
          <div className="col-span-2">
            <span className="text-muted-foreground">Descrizione:</span> {contenzioso.note}
          </div>
          {contenzioso.esito && (
            <div className="col-span-2">
              <span className="text-muted-foreground">Esito:</span> {contenzioso.esito}
            </div>
          )}
          {contenzioso.chiuso_at && (
            <div>
              <span className="text-muted-foreground">Chiuso il:</span>{' '}
              {fmtDate(contenzioso.chiuso_at)}
            </div>
          )}
        </div>
      </Card>

      {/* Timeline azioni */}
      <Card className="overflow-hidden">
        <div className="px-4 py-2 border-b border-border bg-muted/50">
          <h2 className="text-sm font-semibold text-foreground">
            Timeline ({contenzioso.azioni.length} {contenzioso.azioni.length === 1 ? 'azione' : 'azioni'})
          </h2>
        </div>
        {contenzioso.azioni.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            Nessuna azione registrata
          </div>
        ) : (
          <div className="divide-y divide-border">
            {contenzioso.azioni.map((a) => (
              <div key={a.id} className="px-4 py-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground">
                    {TIPO_AZIONE_LABELS[a.tipo_azione] || a.tipo_azione}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {fmtDate(a.created_at)}
                  </span>
                  {a.importo && (
                    <span className="text-xs font-medium text-accent">
                      {currency(a.importo)}
                    </span>
                  )}
                </div>
                <p className="text-sm text-foreground">{a.descrizione}</p>
                {a.documento_path && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Documento: {a.documento_path}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Form nuova azione */}
      {showAzioneForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="p-6 w-[480px] space-y-4">
            <h3 className="text-lg font-outfit font-semibold text-foreground">
              Nuova azione
            </h3>
            <div>
              <label className="text-sm text-muted-foreground">Tipo azione</label>
              <select
                value={tipoAzione}
                onChange={(e) => setTipoAzione(e.target.value)}
                className="w-full mt-1 rounded border border-border bg-card text-foreground px-3 py-2 text-sm"
              >
                {Object.entries(TIPO_AZIONE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Descrizione</label>
              <textarea
                value={descrizione}
                onChange={(e) => setDescrizione(e.target.value)}
                className="w-full mt-1 rounded border border-border bg-card text-foreground px-3 py-2 text-sm"
                rows={3}
                placeholder="Descrivi l'azione..."
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Importo (opzionale)</label>
              <input
                type="number"
                step="0.01"
                value={importoAzione}
                onChange={(e) => setImportoAzione(e.target.value)}
                className="w-full mt-1 rounded border border-border bg-card text-foreground px-3 py-2 text-sm"
                placeholder="0.00"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowAzioneForm(false)}>
                Annulla
              </Button>
              <Button onClick={handleAggiungiAzione} disabled={!descrizione.trim() || actionLoading}>
                Aggiungi
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Form risoluzione */}
      {showRisoluzione && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="p-6 w-[480px] space-y-4">
            <h3 className="text-lg font-outfit font-semibold text-foreground">
              Risolvi contenzioso
            </h3>
            <div>
              <label className="text-sm text-muted-foreground">Esito</label>
              <textarea
                value={esitoRis}
                onChange={(e) => setEsitoRis(e.target.value)}
                className="w-full mt-1 rounded border border-border bg-card text-foreground px-3 py-2 text-sm"
                rows={3}
                placeholder="Descrivi l'esito della risoluzione..."
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Importo risolto (opzionale)</label>
              <input
                type="number"
                step="0.01"
                value={importoRisolto}
                onChange={(e) => setImportoRisolto(e.target.value)}
                className="w-full mt-1 rounded border border-border bg-card text-foreground px-3 py-2 text-sm"
                placeholder="0.00"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowRisoluzione(false)}>
                Annulla
              </Button>
              <Button onClick={handleRisolvi} disabled={!esitoRis.trim() || actionLoading}>
                Risolvi
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
